// src/pages/ListinoMaterialiPage.js
// Acquisti → Listino Materiali. NON è un magazzino: nessuna giacenza, nessun lotto, nessuna
// ubicazione. È l'elenco dei materiali con il loro prezzo di listino, sconto abituale e
// fornitore — alimenta sia il Rientro Materiali (valore economico noto) sia Elaborazione
// Offerte (ricerca rapida invece di scrivere ogni materiale da zero). Gestito solo da
// admin/owner, per tenere i prezzi affidabili.
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, doc, addDoc, updateDoc, deleteDoc, setDoc, serverTimestamp, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { formatValutaItaliana, parseValutaItaliana } from "../utils/clienteHelpers";
import { Plus, X, Trash2, Tags, Pencil, Truck } from "lucide-react";
import { DEFAULT_TIPO_RESO, DEFAULT_AUTOMEZZO_SUGGERITO } from "../utils/rientroMaterialiConstants";
import ImportaExcelModal from "../components/ImportaExcelModal";

// Materiali più utilizzati — aggregati da Elaborazione Offerte e Computo SAL, per far
// vedere quali materiali ricorrono di più nel lavoro reale, e segnalare quelli che
// vengono scritti spesso a mano ma non sono ancora a listino.
// Fornitori più utilizzati — stessi dati di sopra, aggregati per fornitore invece che per
// materiale, per capire quanto ci si affida a ciascuno (utile per negoziare, o accorgersi
// di dipendere troppo da un solo fornitore).
function fmtPerc1(n) {
  return (parseFloat(n)||0).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

// Materiali che rientrano più spesso "in eccesso" o per errore di approvvigionamento —
// stesso dato che l'utente sceglie già ogni volta che registra un rientro (Tipo di reso),
// mai aggregato prima: segnala dove si tende a sovrastimare la quantità nelle offerte.
const TIPI_RESO_SEGNALE = ["Fine lavori / materiale in eccesso", "Errore di approvvigionamento"];
function MaterialiInEccesso({ orgId }) {
  const [aggregati, setAggregati] = useState([]);
  const [caricato, setCaricato] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const snap = await getDocs(query(collection(db,"rientriMateriali"), where("organizationId","==",orgId)));
      const mappa = {};
      snap.docs.forEach(d => {
        const r = d.data();
        const chiave = (r.descrizione||"").trim().toLowerCase();
        if (!chiave) return;
        if (!mappa[chiave]) mappa[chiave] = { descrizioneOriginale:r.descrizione.trim(), totale:0, inEccesso:0 };
        mappa[chiave].totale++;
        if (TIPI_RESO_SEGNALE.includes(r.tipoReso)) mappa[chiave].inEccesso++;
      });
      const risultati = Object.values(mappa)
        .filter(m => m.totale>=2 && m.inEccesso>0)
        .map(m => ({ ...m, percentuale: m.inEccesso/m.totale*100 }))
        .sort((a,b)=>b.percentuale-a.percentuale || b.inEccesso-a.inEccesso);
      setAggregati(risultati);
      setCaricato(true);
    })();
  }, [orgId]);

  if (!caricato || aggregati.length===0) return null;

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20,marginBottom:20}}>
      <h3 style={{fontSize:14,fontWeight:700,color:"#1e1b4b",marginBottom:4}}>📉 Materiali con più rientri in eccesso</h3>
      <p style={{fontSize:11.5,color:"#6b7280",marginBottom:14}}>Dai rientri registrati con causale "Fine lavori / materiale in eccesso" o "Errore di approvvigionamento" — un segnale che nelle offerte si tende a sovrastimare la quantità.</p>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead><tr style={{borderBottom:"2px solid #f3f4f6"}}>
          {["Materiale","Rientri in eccesso","Su totale rientri","% in eccesso"].map(h=>(
            <th key={h} style={{padding:"6px 8px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {aggregati.slice(0,10).map((m,i)=>(
            <tr key={m.descrizioneOriginale} style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6"}}>
              <td style={{padding:"7px 8px",fontWeight:600,color:"#111827"}}>{m.descrizioneOriginale}</td>
              <td style={{padding:"7px 8px",color:"#374151"}}>{m.inEccesso}</td>
              <td style={{padding:"7px 8px",color:"#374151"}}>{m.totale}</td>
              <td style={{padding:"7px 8px",fontWeight:700,color:m.percentuale>=50?"#dc2626":"#d97706"}}>{m.percentuale.toFixed(0)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FornitoriPiuUtilizzati({ orgId, voci }) {
  const [aggregati, setAggregati] = useState([]);
  const [caricato, setCaricato] = useState(false);

  useEffect(() => {
    if (!orgId || !voci) return;
    (async () => {
      const [offSnap, salSnap] = await Promise.all([
        getDocs(query(collection(db,"elaborazioniOfferte"), where("orgId","==",orgId))),
        getDocs(query(collection(db,"computiSAL"), where("orgId","==",orgId))),
      ]);
      const listinoMappa = {}; // descrizione normalizzata -> voce listino (con fornitore)
      voci.forEach(v => { listinoMappa[v.descrizione.trim().toLowerCase()] = v; });

      const perFornitore = {}; // fornitore -> {valore, materiali:Set}
      const registra = (descrizione, quantita, prezzo) => {
        const chiave = (descrizione||"").trim().toLowerCase();
        const voceListino = listinoMappa[chiave];
        if (!voceListino?.fornitore) return; // solo materiali riconducibili a un fornitore noto
        const valore = (parseFloat(quantita)||0) * (parseFloat(prezzo)||voceListino.prezzoUnitario||0);
        if (!perFornitore[voceListino.fornitore]) perFornitore[voceListino.fornitore] = { valore:0, materiali:new Set() };
        perFornitore[voceListino.fornitore].valore += valore;
        perFornitore[voceListino.fornitore].materiali.add(chiave);
      };
      offSnap.docs.forEach(d => (d.data().materiali||[]).forEach(m => registra(m.descrizione, m.quantita, m.prezzoListino)));
      salSnap.docs.forEach(d => (d.data().righe||[]).forEach(r => { if(!r.titolo) registra(r.descrizione, r.quantita, r.prezzoUnitario); }));

      const totale = Object.values(perFornitore).reduce((s,f)=>s+f.valore,0);
      const risultati = Object.entries(perFornitore)
        .map(([fornitore,f]) => ({ fornitore, valore:f.valore, nMateriali:f.materiali.size, percentuale: totale>0?(f.valore/totale*100):0 }))
        .sort((a,b)=>b.valore-a.valore);
      setAggregati(risultati);
      setCaricato(true);
    })();
  }, [orgId, voci]);

  if (!caricato || aggregati.length===0) return null;
  const fornitoreDominante = aggregati[0]?.percentuale>=50 ? aggregati[0] : null;

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20,marginBottom:20}}>
      <h3 style={{fontSize:14,fontWeight:700,color:"#1e1b4b",marginBottom:4}}>🏭 Fornitori più utilizzati</h3>
      <p style={{fontSize:11.5,color:"#6b7280",marginBottom:14}}>Valore dei materiali riconducibili a ciascun fornitore, da Elaborazione Offerte e Computo Metrico/SAL — solo per i materiali già collegati a un fornitore nel Listino.</p>
      {fornitoreDominante && (
        <div style={{background:"#fef3c7",border:"1px solid #fde68a",borderRadius:8,padding:"8px 12px",marginBottom:14,fontSize:11.5,color:"#92400e"}}>
          ⚠️ Oltre metà del valore ({fmtPerc1(fornitoreDominante.percentuale)}%) viene da un solo fornitore: <strong>{fornitoreDominante.fornitore}</strong>.
        </div>
      )}
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead><tr style={{borderBottom:"2px solid #f3f4f6"}}>
          {["Fornitore","Materiali diversi","Valore","% sul totale"].map(h=>(
            <th key={h} style={{padding:"6px 8px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {aggregati.map((f,i)=>(
            <tr key={f.fornitore} style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6"}}>
              <td style={{padding:"7px 8px",fontWeight:600,color:"#111827"}}>{f.fornitore}</td>
              <td style={{padding:"7px 8px",color:"#374151"}}>{f.nMateriali}</td>
              <td style={{padding:"7px 8px",fontWeight:700,color:"#059669"}}>€{formatValutaItaliana(f.valore)}</td>
              <td style={{padding:"7px 8px",fontWeight:700,color:f.percentuale>=50?"#d97706":"#7c3aed"}}>{fmtPerc1(f.percentuale)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MaterialiPiuUtilizzati({ orgId, voci, onAggiungiRapido }) {
  const [aggregati, setAggregati] = useState([]);
  const [caricato, setCaricato] = useState(false);
  const [mostraTutti, setMostraTutti] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const [offSnap, salSnap] = await Promise.all([
        getDocs(query(collection(db,"elaborazioniOfferte"), where("orgId","==",orgId))),
        getDocs(query(collection(db,"computiSAL"), where("orgId","==",orgId))),
      ]);
      const mappa = {};
      const registra = (descrizione, quantita, prezzo) => {
        const chiave = (descrizione||"").trim().toLowerCase();
        if (!chiave) return;
        if (!mappa[chiave]) mappa[chiave] = { descrizioneOriginale:descrizione.trim(), occorrenze:0, quantitaTotale:0, sommaPrezzi:0, nPrezzi:0 };
        mappa[chiave].occorrenze++;
        mappa[chiave].quantitaTotale += parseFloat(quantita)||0;
        if (parseFloat(prezzo)>0) { mappa[chiave].sommaPrezzi += parseFloat(prezzo); mappa[chiave].nPrezzi++; }
      };
      offSnap.docs.forEach(d => (d.data().materiali||[]).forEach(m => registra(m.descrizione, m.quantita, m.prezzoListino)));
      salSnap.docs.forEach(d => (d.data().righe||[]).forEach(r => { if(!r.titolo) registra(r.descrizione, r.quantita, r.prezzoUnitario); }));

      const listinoSet = new Set((voci||[]).map(v=>v.descrizione.trim().toLowerCase()));
      const risultati = Object.values(mappa)
        .filter(m=>m.occorrenze>=2)
        .map(m => ({ ...m, prezzoMedio: m.nPrezzi>0?m.sommaPrezzi/m.nPrezzi:0, aListino: listinoSet.has(m.descrizioneOriginale.toLowerCase()) }))
        .sort((a,b)=>b.occorrenze-a.occorrenze);
      setAggregati(risultati);
      setCaricato(true);
    })();
  }, [orgId, voci]);

  if (!caricato || aggregati.length===0) return null;
  const daMostrare = mostraTutti ? aggregati : aggregati.slice(0,10);

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20,marginBottom:20}}>
      <h3 style={{fontSize:14,fontWeight:700,color:"#1e1b4b",marginBottom:4}}>📊 Materiali più utilizzati</h3>
      <p style={{fontSize:11.5,color:"#6b7280",marginBottom:14}}>Da Elaborazione Offerte e Computo Metrico/SAL, tutti insieme — solo materiali usati almeno 2 volte.</p>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead><tr style={{borderBottom:"2px solid #f3f4f6"}}>
          {["Materiale","Volte usato","Quantità totale","Prezzo medio","Listino",""].map(h=>(
            <th key={h} style={{padding:"6px 8px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {daMostrare.map((m,i) => (
            <tr key={i} style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6"}}>
              <td style={{padding:"7px 8px",fontWeight:600,color:"#111827"}}>{m.descrizioneOriginale}</td>
              <td style={{padding:"7px 8px",color:"#374151"}}>{m.occorrenze}</td>
              <td style={{padding:"7px 8px",color:"#374151"}}>{m.quantitaTotale>0?m.quantitaTotale:"—"}</td>
              <td style={{padding:"7px 8px",fontWeight:600,color:"#059669"}}>{m.prezzoMedio>0?`€${formatValutaItaliana(m.prezzoMedio)}`:"—"}</td>
              <td style={{padding:"7px 8px"}}>
                {m.aListino
                  ? <span style={{fontSize:10,padding:"2px 7px",borderRadius:8,background:"#d1fae5",color:"#065f46",fontWeight:600}}>Già a listino</span>
                  : <span style={{fontSize:10,padding:"2px 7px",borderRadius:8,background:"#fef3c7",color:"#92400e",fontWeight:600}}>Non a listino</span>}
              </td>
              <td style={{padding:"7px 8px"}}>
                {!m.aListino && (
                  <button onClick={()=>onAggiungiRapido(m)} style={{fontSize:10.5,fontWeight:700,color:"#7c3aed",background:"none",border:"1px solid #e9d5ff",borderRadius:6,padding:"3px 8px",cursor:"pointer"}}>
                    + Aggiungi
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {aggregati.length>10 && (
        <button onClick={()=>setMostraTutti(v=>!v)} style={{marginTop:10,fontSize:11.5,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>
          {mostraTutti?"Mostra solo i primi 10":`Mostra tutti (${aggregati.length})`}
        </button>
      )}
    </div>
  );
}

export const UM_OPTIONS = ["pz","mt","ml","kg","lt","rotoli","scatole","sacchi","m²","altro"];
const LIMITE_RIGHE = 600;

const inp = {width:"100%",padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"};

// Piccolo editor "a tag": aggiungi/rimuovi voci di una lista testuale (usato per Tipi di reso e Automezzi)
function EditorListaTag({ titolo, valori, onChange }) {
  const [nuovo, setNuovo] = useState("");
  const aggiungi = () => {
    const v = nuovo.trim();
    if(!v || valori.includes(v)) return;
    onChange([...valori, v]);
    setNuovo("");
  };
  return (
    <div>
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
        {valori.map(v=>(
          <span key={v} style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:14,background:"#ede9fe",color:"#7c3aed",fontSize:12,fontWeight:600}}>
            {v}
            <X size={12} style={{cursor:"pointer"}} onClick={()=>onChange(valori.filter(x=>x!==v))}/>
          </span>
        ))}
        {valori.length===0 && <span style={{fontSize:12,color:"#9ca3af"}}>Nessuna voce</span>}
      </div>
      <div style={{display:"flex",gap:6}}>
        <input style={{...inp,flex:1}} value={nuovo} onChange={e=>setNuovo(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); aggiungi(); } }}
          placeholder={`Aggiungi ${titolo.toLowerCase()}...`}/>
        <button type="button" onClick={aggiungi} style={{padding:"0 12px",border:"1px solid #7c3aed",borderRadius:8,background:"#7c3aed",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>+</button>
      </div>
    </div>
  );
}

export default function ListinoMaterialiPage() {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const canGestire = isOrgAdmin || isPlatformOwner;

  const [voci, setVoci] = useState([]);
  const [editRow, setEditRow] = useState(null); // {} = nuova, {id,...} = modifica
  const [form, setForm] = useState({ descrizione:"", codiceArticolo:"", unitaMisura:"pz", prezzoUnitario:"", scontoAbituale:"", fornitore:"", categoria:"", note:"" });
  const [cerca, setCerca] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [tipiReso, setTipiReso] = useState(DEFAULT_TIPO_RESO);
  const [automezzi, setAutomezzi] = useState(DEFAULT_AUTOMEZZO_SUGGERITO);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    onSnapshot(query(collection(db,"listinoMateriali"),where("organizationId","==",effectiveOrgId)),
      snap=>setVoci(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.descrizione||"").localeCompare(b.descrizione||"","it"))));
    return onSnapshot(doc(db,"configRientroMateriali",effectiveOrgId), snap=>{
      if(snap.exists()){
        const d = snap.data();
        setTipiReso(d.tipiReso?.length ? d.tipiReso : DEFAULT_TIPO_RESO);
        setAutomezzi(d.automezzi?.length ? d.automezzi : DEFAULT_AUTOMEZZO_SUGGERITO);
      }
    });
  },[effectiveOrgId]);

  const salvaConfigRientro = async (campo, valori) => {
    await setDoc(doc(db,"configRientroMateriali",effectiveOrgId), { [campo]:valori, organizationId:effectiveOrgId, updatedAt:serverTimestamp() }, { merge:true });
  };

  const apriNuova = () => {
    if (voci.length >= LIMITE_RIGHE) { addToast(`Limite di ${LIMITE_RIGHE} voci raggiunto.`,"error"); return; }
    setForm({ descrizione:"", codiceArticolo:"", unitaMisura:"pz", prezzoUnitario:"", scontoAbituale:"", fornitore:"", categoria:"", note:"" });
    setEditRow({});
  };
  const apriModifica = (v) => { setForm({ descrizione:v.descrizione, codiceArticolo:v.codiceArticolo||"", unitaMisura:v.unitaMisura, prezzoUnitario:String(v.prezzoUnitario||""), scontoAbituale:String(v.scontoAbituale||""), fornitore:v.fornitore||"", categoria:v.categoria||"", note:v.note||"" }); setEditRow(v); };

  const salva = async () => {
    if(!form.descrizione.trim()){ addToast("Descrizione obbligatoria","error"); return; }
    const prezzo = parseValutaItaliana(form.prezzoUnitario);
    if(!prezzo || prezzo<=0){ addToast("Inserisci un prezzo unitario valido","error"); return; }
    // evita doppioni per descrizione (case-insensitive)
    const doppione = voci.find(v=>v.id!==editRow.id && v.descrizione.trim().toLowerCase()===form.descrizione.trim().toLowerCase());
    if(doppione){ addToast("Esiste già una voce con questa descrizione — modificala invece di duplicarla","error"); return; }
    // stesso controllo sul codice articolo, solo se compilato (è facoltativo)
    if(form.codiceArticolo.trim()){
      const doppioneCodice = voci.find(v=>v.id!==editRow.id && v.codiceArticolo?.trim().toLowerCase()===form.codiceArticolo.trim().toLowerCase());
      if(doppioneCodice){ addToast(`Il codice "${form.codiceArticolo.trim()}" è già usato da "${doppioneCodice.descrizione}"`,"error"); return; }
    }
    const dati = {
      descrizione: form.descrizione.trim(), codiceArticolo: form.codiceArticolo.trim(), unitaMisura: form.unitaMisura,
      prezzoUnitario: prezzo, scontoAbituale: parseFloat(form.scontoAbituale)||0,
      fornitore: form.fornitore.trim(), categoria: form.categoria.trim(), note: form.note.trim(),
      organizationId: effectiveOrgId, updatedAt: serverTimestamp(), updatedBy: currentUser?.displayName||"",
    };
    if(editRow.id){
      await updateDoc(doc(db,"listinoMateriali",editRow.id), dati);
      addToast("Voce aggiornata ✅","success");
    } else {
      await addDoc(collection(db,"listinoMateriali"), { ...dati, createdAt:serverTimestamp(), createdBy:currentUser?.displayName||"" });
      addToast("Voce aggiunta ✅","success");
    }
    setEditRow(null);
  };

  const elimina = async (v) => {
    if(!await confirm(`Eliminare "${v.descrizione}" dal listino?`,{type:"danger"})) return;
    await deleteDoc(doc(db,"listinoMateriali",v.id));
    addToast("Eliminata","info");
  };

  if(!canGestire){
    return (
      <div style={{padding:40,textAlign:"center",color:"#9ca3af"}}>
        <Tags size={32} style={{opacity:.3,marginBottom:8}}/>
        <div style={{fontSize:14,fontWeight:600}}>Sezione riservata agli amministratori</div>
      </div>
    );
  }

  const categorieDisponibili = [...new Set(voci.map(v=>v.categoria).filter(Boolean))].sort((a,b)=>a.localeCompare(b,"it"));
  const vociFiltrate = voci.filter(v => {
    if (filtroCategoria && v.categoria!==filtroCategoria) return false;
    if (cerca) {
      const s = cerca.toLowerCase();
      return (v.descrizione||"").toLowerCase().includes(s) || (v.codiceArticolo||"").toLowerCase().includes(s) || (v.fornitore||"").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div style={{padding:24,maxWidth:1100,flex:1,overflowY:"auto"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6,flexWrap:"wrap",gap:10}}>
        <h2 style={{fontSize:18,fontWeight:800,display:"flex",alignItems:"center",gap:8,margin:0}}>
          <Tags size={18} color="#7c3aed"/> Listino Materiali
        </h2>
        <div style={{display:"flex",gap:8}}>
          <button onClick={apriNuova} className="btn btn-primary" style={{display:"flex",alignItems:"center",gap:5}}>
            <Plus size={14}/> Nuova voce
          </button>
          <ImportaExcelModal
            label="Importa listino da Excel"
            campiMappabili={[
              {key:"descrizione",label:"Descrizione",indovina:/descriz/},
              {key:"codiceArticolo",label:"Codice articolo",indovina:/codice|cod\.?\s*art/},
              {key:"unitaMisura",label:"Unità di Misura",indovina:/^u\.?m\.?$|unit.*misura/},
              {key:"prezzoUnitario",label:"Prezzo di listino",indovina:/prezzo/},
              {key:"scontoAbituale",label:"Sconto abituale %",indovina:/sconto/},
              {key:"fornitore",label:"Fornitore",indovina:/fornitore/},
              {key:"categoria",label:"Categoria",indovina:/categoria/},
              {key:"note",label:"Note",indovina:/note/},
            ]}
            campoObbligatorio="descrizione"
            avvisoIniziale="Funziona bene se il tuo file ha una riga per ogni articolo, con i dati in colonne. Fogli con celle unite o strutture particolari potrebbero non importarsi correttamente — in quel caso conviene inserirli a mano la prima volta."
            avvisoFinale="Le voci con la stessa descrizione o lo stesso codice articolo di una già presente vengono saltate automaticamente, come quando la aggiungi a mano — non creano doppioni."
            onConferma={async (righeMappate) => {
              let creati=0, saltatiDuplicati=0, saltatiPrezzo=0;
              // Tengo traccia anche dei duplicati DENTRO lo stesso file, non solo di quelli già in Listino
              const descrizioniViste = new Set(voci.map(v=>v.descrizione.trim().toLowerCase()));
              const codiciVisti = new Set(voci.map(v=>v.codiceArticolo?.trim().toLowerCase()).filter(Boolean));
              for (const r of righeMappate) {
                const prezzo = parseValutaItaliana(r.prezzoUnitario||"0");
                if (!prezzo || prezzo<=0) { saltatiPrezzo++; continue; }
                const descNorm = (r.descrizione||"").trim().toLowerCase();
                const codNorm = (r.codiceArticolo||"").trim().toLowerCase();
                if (descrizioniViste.has(descNorm) || (codNorm && codiciVisti.has(codNorm))) { saltatiDuplicati++; continue; }
                if (voci.length+creati >= LIMITE_RIGHE) break;
                descrizioniViste.add(descNorm);
                if (codNorm) codiciVisti.add(codNorm);
                await addDoc(collection(db,"listinoMateriali"), {
                  descrizione:(r.descrizione||"").trim(), codiceArticolo:(r.codiceArticolo||"").trim(),
                  unitaMisura:(r.unitaMisura||"pz").trim(), prezzoUnitario:prezzo,
                  scontoAbituale:parseFloat(r.scontoAbituale)||0, fornitore:(r.fornitore||"").trim(),
                  categoria:(r.categoria||"").trim(), note:(r.note||"").trim(),
                  organizationId: effectiveOrgId, createdAt:serverTimestamp(), createdBy:currentUser?.displayName||"",
                });
                creati++;
              }
              let msg = `${creati} voce/i importate ✅`;
              if (saltatiDuplicati>0) msg += ` — ${saltatiDuplicati} saltate perché già presenti`;
              if (saltatiPrezzo>0) msg += ` — ${saltatiPrezzo} saltate senza prezzo valido`;
              addToast(msg,"info");
            }}
          />
        </div>
      </div>
      <p style={{fontSize:12,color:"#9ca3af",marginBottom:14}}>
        Materiali con prezzo di listino, sconto abituale e fornitore — <strong>non è un magazzino</strong>: nessuna giacenza, nessun lotto, nessuna ubicazione. Alimenta Rientro Materiali ed Elaborazione Offerte. {voci.length}/{LIMITE_RIGHE} voci.
      </p>

      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
        <input value={cerca} onChange={e=>setCerca(e.target.value)} placeholder="Cerca per descrizione, codice, fornitore..."
          style={{...inp,width:280}}/>
        <select value={filtroCategoria} onChange={e=>setFiltroCategoria(e.target.value)} style={{...inp,width:180}}>
          <option value="">Tutte le categorie</option>
          {categorieDisponibili.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        {(cerca||filtroCategoria) && (
          <button onClick={()=>{setCerca("");setFiltroCategoria("");}} style={{padding:"6px 12px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",fontSize:12,cursor:"pointer"}}>× Reset</button>
        )}
      </div>

      {voci.length===0 ? (
        <div style={{textAlign:"center",padding:50,color:"#9ca3af",border:"1px dashed #e5e7eb",borderRadius:10}}>
          Nessuna voce ancora. Aggiungi i materiali che comprate più spesso, o importa un listino da Excel.
        </div>
      ) : vociFiltrate.length===0 ? (
        <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Nessuna voce corrisponde ai filtri</div>
      ) : (
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5}}>
          <thead>
            <tr style={{borderBottom:"2px solid #e5e7eb"}}>
              <th style={{textAlign:"left",padding:"7px 8px",fontSize:10.5,color:"#374151",fontWeight:700}}>Descrizione</th>
              <th style={{textAlign:"left",padding:"7px 8px",fontSize:10.5,color:"#374151",fontWeight:700}}>Codice</th>
              <th style={{textAlign:"left",padding:"7px 8px",fontSize:10.5,color:"#374151",fontWeight:700}}>U.M.</th>
              <th style={{textAlign:"right",padding:"7px 8px",fontSize:10.5,color:"#374151",fontWeight:700}}>Prezzo listino</th>
              <th style={{textAlign:"right",padding:"7px 8px",fontSize:10.5,color:"#374151",fontWeight:700}}>Sconto %</th>
              <th style={{textAlign:"left",padding:"7px 8px",fontSize:10.5,color:"#374151",fontWeight:700}}>Fornitore</th>
              <th style={{textAlign:"left",padding:"7px 8px",fontSize:10.5,color:"#374151",fontWeight:700}}>Categoria</th>
              <th style={{width:70}}></th>
            </tr>
          </thead>
          <tbody>
            {vociFiltrate.map(v=>(
              <tr key={v.id} style={{borderBottom:"1px solid #f3f4f6"}}>
                <td style={{padding:"7px 8px",fontWeight:600,color:"#111827"}}>{v.descrizione}</td>
                <td style={{padding:"7px 8px",color:"#6b7280",fontFamily:"monospace",fontSize:11}}>{v.codiceArticolo||"—"}</td>
                <td style={{padding:"7px 8px",color:"#6b7280"}}>{v.unitaMisura}</td>
                <td style={{padding:"7px 8px",textAlign:"right",fontWeight:700,color:"#059669"}}>€ {formatValutaItaliana(v.prezzoUnitario)}</td>
                <td style={{padding:"7px 8px",textAlign:"right",color:v.scontoAbituale>0?"#d97706":"#d1d5db"}}>{v.scontoAbituale>0?`${v.scontoAbituale}%`:"—"}</td>
                <td style={{padding:"7px 8px",color:"#6b7280"}}>{v.fornitore||"—"}</td>
                <td style={{padding:"7px 8px"}}>{v.categoria?<span style={{fontSize:10.5,padding:"2px 7px",borderRadius:8,background:"#f5f3ff",color:"#5b21b6",fontWeight:600}}>{v.categoria}</span>:"—"}</td>
                <td style={{padding:"7px 8px",display:"flex",gap:6,justifyContent:"flex-end"}}>
                  <button onClick={()=>apriModifica(v)} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><Pencil size={14}/></button>
                  <button onClick={()=>elimina(v)} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5"}}><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <MaterialiPiuUtilizzati orgId={effectiveOrgId} voci={voci} onAggiungiRapido={(m)=>{
        setForm({ descrizione:m.descrizioneOriginale, codiceArticolo:"", unitaMisura:"pz", prezzoUnitario: m.prezzoMedio>0?String(m.prezzoMedio.toFixed(2)).replace(".",","):"", scontoAbituale:"", fornitore:"", categoria:"", note:"" });
        setEditRow({});
      }}/>

      <MaterialiInEccesso orgId={effectiveOrgId}/>

      <FornitoriPiuUtilizzati orgId={effectiveOrgId} voci={voci}/>

      <div style={{marginTop:32,paddingTop:20,borderTop:"1px solid #e5e7eb"}}>
        <h3 style={{fontSize:14,fontWeight:800,display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
          <Truck size={15} color="#7c3aed"/> Tipi di reso e automezzi (Rientro Materiali)
        </h3>
        <p style={{fontSize:12,color:"#9ca3af",marginBottom:14}}>
          Personalizza le voci proposte nel modulo Rientro Materiali in base al vostro parco mezzi e alle vostre casistiche — sono precompilate con valori generici, modificale liberamente.
        </p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
          <div>
            <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:8}}>Tipi di reso</label>
            <EditorListaTag titolo="Tipo di reso" valori={tipiReso} onChange={v=>{ setTipiReso(v); salvaConfigRientro("tipiReso",v); }}/>
          </div>
          <div>
            <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:8}}>Automezzi suggeriti</label>
            <EditorListaTag titolo="Automezzo" valori={automezzi} onChange={v=>{ setAutomezzi(v); salvaConfigRientro("automezzi",v); }}/>
          </div>
        </div>
      </div>

      {editRow && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setEditRow(null)}>
          <div className="modal" style={{maxWidth:420}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <h3 style={{fontSize:15,fontWeight:700}}>{editRow.id?"Modifica voce":"Nuova voce listino"}</h3>
              <button onClick={()=>setEditRow(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={16}/></button>
            </div>
            <div style={{marginBottom:12}}>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Descrizione *</label>
              <input style={inp} value={form.descrizione} onChange={e=>setForm(f=>({...f,descrizione:e.target.value}))} placeholder="Es. Cavo FG16OR16 3G2.5mm²"/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Codice articolo</label>
                <input style={inp} value={form.codiceArticolo} onChange={e=>setForm(f=>({...f,codiceArticolo:e.target.value}))} placeholder="Es. CV-2544"/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Unità di misura</label>
                <select style={inp} value={form.unitaMisura} onChange={e=>setForm(f=>({...f,unitaMisura:e.target.value}))}>
                  {UM_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Prezzo di listino (€) *</label>
                <input style={inp} type="text" inputMode="decimal" value={form.prezzoUnitario} onChange={e=>setForm(f=>({...f,prezzoUnitario:e.target.value}))} placeholder="Es. 1,25"/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Sconto abituale (%)</label>
                <input style={inp} type="number" min="0" max="100" step="0.1" value={form.scontoAbituale} onChange={e=>setForm(f=>({...f,scontoAbituale:e.target.value}))} placeholder="Es. 15"/>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Fornitore</label>
                <input style={inp} value={form.fornitore} onChange={e=>setForm(f=>({...f,fornitore:e.target.value}))} placeholder="Es. Sonepar"/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Categoria</label>
                <input style={inp} value={form.categoria} onChange={e=>setForm(f=>({...f,categoria:e.target.value}))} placeholder="Es. Cavi"/>
              </div>
            </div>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Note (opzionale)</label>
              <input style={inp} value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} placeholder="Es. prezzo medio, aggiornato luglio 2026..."/>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="btn btn-secondary" onClick={()=>setEditRow(null)}>Annulla</button>
              <button className="btn btn-primary" onClick={salva}>💾 Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
